import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import {
  useSelector as useReduxSelector,
  TypedUseSelectorHook,
} from 'react-redux';
import { showDialog } from 'stores/dialogSlice';
import { AppState, useAppDispatch } from 'stores/type';
import { LoginPageLayout } from 'views/layouts/LoginPageLayout';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { Redirect } from 'react-router-dom';
import { DeviceDomain } from 'models/Device';
import { useIntl } from 'react-intl';
import { OAuthDomain } from 'models/OAuth';
import { setExceptionError } from 'stores/errorSlice';
import { ROUTER_PATH } from 'views/constants';
import { InvitationDomain } from 'models/Invitation';
import { Splash } from 'views/components/molecules/Splash';
import { Invitation } from 'models/Invitation/type';
import { useDevice } from 'views/hooks/useDevice';
import { ApplicationError } from 'models/Error';
import { localeIds } from 'locals';

export const LoginPage: React.FC = () => {
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const deviceDomain = new DeviceDomain(device);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [invitation, setInvitation] = useState<Invitation | undefined>(
    undefined
  );
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { login } = useDevice();
  const invitationToken = queryString.parse(location.search)[
    'invitation_token'
  ] as string;

  useEffect(() => {
    const getDetail = async () => {
      InvitationDomain.detail(invitationToken)
        .then((result) => {
          if (result.isSuccess()) {
            setInvitation(result.value);
          }

          setIsLoading(false);
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    };

    if (invitationToken) {
      getDetail();
    } else {
      setIsLoading(false);
    }
  }, [dispatch, invitationToken]);

  if (
    deviceDomain.isAuthenticated() ||
    deviceDomain.hasIphoneRegistrationToken()
  ) {
    return <Redirect to={ROUTER_PATH.apps} />;
  }

  if (isLoading) {
    return (
      <DefaultTemplate headerText="">
        <Splash />
      </DefaultTemplate>
    );
  } else {
    return (
      <DefaultTemplate headerText="DeployGate" showLogo>
        <LoginPageLayout
          defaultEmail={invitation?.email}
          errorMessage={errorMessage}
          onLogin={async (email: string, password: string) => {
            await login(email, password, invitation?.token).catch((e) => {
              if (e instanceof ApplicationError) {
                setErrorMessage(intl.formatMessage({ id: e.localeId() }));
              } else {
                dispatch(setExceptionError(e));
              }
            });
          }}
          onGoogleLogin={async () => {
            const url = await OAuthDomain.loginURL('google');
            location.replace(url);
          }}
          onGitHubLogin={async () => {
            const url = await OAuthDomain.loginURL('github');
            location.replace(url);
          }}
          onChooseNoAccount={async () => {
            dispatch(
              showDialog({
                title: intl.formatMessage({
                  id: localeIds.loginPage.nonAccountHolderNoticeTitle,
                }),
                content: intl.formatMessage({
                  id: localeIds.loginPage.nonAccountHolderNoticeContent,
                }),
              })
            );
          }}
        />
      </DefaultTemplate>
    );
  }
};
